import { Card, Typography } from "../material-old";
import {
  Button,
  Form,
  FormProps,
  Input,
  message,
  Select,
  Space,
  Switch,
} from "antd";
import React, { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks.ts";
import {
  fetchUserByID,
  updateUser,
} from "../../redux/actions/usersActions.tsx";
import { CoreLoading } from "../common/CoreLoading.tsx";
import { extractErrorMessage } from "../../utils/extractErrorMessage.ts";
import {
  CalendarOutlined,
  MailFilled,
  SaveFilled,
  UsergroupAddOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { BreadcrumbComponent } from "./BreadcrumbComponent.tsx";
import { useLocation, useNavigate } from "react-router-dom";
import { darkBlue, orange } from "../../utils/theme/colors.ts";
import { UserRole } from "../../redux/interfaces/auth.types.ts";

interface Props {
  id?: string;
  isProfile?: boolean;
}

type FormValues = {
  name: string;
  email: string;
  groups: UserRole[];
  is_on_leave: boolean;
};

export const Edit: React.FC<Props> = ({ id, isProfile }) => {
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();

  const [messageApi, contextHolder] = message.useMessage();
  const { loading } = useAppSelector((state) => state.user);
  const { activeUser: user } = useAppSelector((state) => state.user);

  useEffect(() => {
    handleInitializeUser();
  }, [pathname]);

  const handleInitializeUser = async () => {
    if (!id) return;

    try {
      if (pathname.includes("users/") || pathname.includes("profile/edit")) {
        const user = await dispatch(fetchUserByID(id)).unwrap();
        form.setFieldsValue({
          name: user.name,
          email: user.email,
          groups: user.groups,
          is_on_leave: user.is_on_leave,
        });
      }
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudo cargar el usuario."));
    }
  };

  const onBack = () => navigate(-1);

  const onUpdateUser: FormProps<FormValues>["onFinish"] = async (values) => {
    try {
      await dispatch(updateUser({ id: id!, ...values })).unwrap();

      messageApi.success("Usuario actualizado correctamente");
    } catch (error: any) {
      messageApi.error(extractErrorMessage(error, "No se pudo actualizar el usuario."));
    }
  };

  if (loading || !user) return <CoreLoading size={1} />;

  const userHead = `${user.name} - ${user.email}`;
  const enabledGroupEdit = user.groups.includes("admin");

  return (
    <Card className={`mx-auto py-8 px-20 md:w-8/12 mt-12`}>
      {contextHolder}
      {
        <div className="flex justify-end">
          <BreadcrumbComponent
            pathText={isProfile ? "Mi perfil" : userHead}
            isProfile={isProfile}
          />
        </div>
      }
      <div className="text-center mt-4">
        <Space>
          <Typography variant="h5" color="blue-gray">
            {user.name}
          </Typography>
        </Space>
      </div>
      <hr className="mt-4" />

      <Form
        className="text-sm mt-8 flex flex-col w-full"
        form={form}
        onFinish={onUpdateUser}
      >
        <Form.Item<FormValues>
          name="name"
          labelCol={{ span: 24 }}
          label={
            <div>
              <UserOutlined className="w-5 h-5" style={{ color: darkBlue }} />
              <span>
                <b>Nombre:</b>
              </span>
            </div>
          }
          rules={[{ required: true, message: "Campo requerido." }]}
          className="mt-2"
        >
          <Input />
        </Form.Item>

        <Form.Item<FormValues>
          name="email"
          labelCol={{ span: 24 }}
          label={
            <div>
              <MailFilled className="w-5 h-5" style={{ color: orange }} />
              <span>
                <b>Correo:</b>
              </span>
            </div>
          }
          rules={[
            { required: true, message: "Campo requerido." },
            { type: "email", message: "Ingresa un email válido." },
          ]}
          className="mt-2"
        >
          <Input type="email" className="lowercase" />
        </Form.Item>

        <Form.Item<FormValues>
          name="groups"
          labelCol={{ span: 24 }}
          label={
            <div>
              <UsergroupAddOutlined
                className="w-5 h-5"
                style={{ color: "#000" }}
              />
              <span>
                <b>Grupos:</b>
              </span>
            </div>
          }
          rules={[{ required: true, message: "Campo requerido." }]}
          className="mt-2"
        >
          <Select
            mode="multiple"
            disabled={!enabledGroupEdit}
            placeholder="Selecciona un grupo"
          >
            <Select.Option value="admin">Administrador</Select.Option>
            <Select.Option value="operations">Operaciones</Select.Option>
            <Select.Option value="cja">CJA</Select.Option>
          </Select>
        </Form.Item>

        <Form.Item<FormValues>
          name="is_on_leave"
          labelCol={{ span: 24 }}
          label={
            <div>
              <CalendarOutlined
                className="w-5 h-5"
                style={{ color: "#52c41a" }}
              />
              <span>
                <b>En vacaciones:</b>
              </span>
            </div>
          }
          className="mt-2"
          valuePropName="checked"
        >
          <Switch checkedChildren="Sí" unCheckedChildren="No" />
        </Form.Item>
      </Form>

      <Space className="w-full flex justify-end">
        <Button type="default" onClick={onBack}>
          Volver
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          onClick={() => form.submit()}
          icon={<SaveFilled />}
        >
          Guardar
        </Button>
      </Space>
    </Card>
  );
};
